/**
 * Pager — offset paging in the table dialect: the range on the left,
 * Previous and Next on the right. It renders whenever there is somewhere
 * else to go, so a page that polling emptied out, or a bookmarked offset
 * past the end, still offers Previous instead of trapping the reader on an
 * empty page.
 */

import { Button } from '@/components/ui/button'

interface PagerProps {
  offset: number
  /** Rows on the current page. */
  count: number
  total: number
  pageSize: number
  onOffsetChange: (offset: number) => void
}

export function Pager({
  offset,
  count,
  total,
  pageSize,
  onOffsetChange,
}: PagerProps): React.JSX.Element | null {
  const hasPrevious = offset > 0
  const hasNext = offset + count < total
  if (!hasPrevious && !hasNext) return null
  const range =
    count === 0
      ? `0 of ${total.toLocaleString()}`
      : `${(offset + 1).toLocaleString()}–${(offset + count).toLocaleString()} of ${total.toLocaleString()}`
  return (
    <div className="flex items-center justify-between">
      <span className="font-mono text-xs text-muted-foreground">{range}</span>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={!hasPrevious}
          onClick={() => {
            onOffsetChange(Math.max(0, offset - pageSize))
          }}
        >
          Previous
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={!hasNext}
          onClick={() => {
            onOffsetChange(offset + pageSize)
          }}
        >
          Next
        </Button>
      </div>
    </div>
  )
}
